import React, { useRef, useCallback, useMemo, useState } from "react";
import { Box, InputAdornment } from "@mui/material";
import { StriimDataTable, StriimTypography, StriimInputField } from "@striim/striim-ui";
import SearchIcon from "@mui/icons-material/Search";
import WithTooltip from "src/generic/tooltip/tooltip";
import App from "app";
import { sxStyles } from "./table-selection-data-table.styles";
import { SchemaModel, TableModel, TableErrors } from "../types";
import type {
    ICellRendererParams,
    GetRowIdParams,
    GridReadyEvent,
    FirstDataRenderedEvent,
    RowSelectedEvent,
    SelectionChangedEvent,
    GridApi,
    RowNode,
    ITooltipParams
} from "ag-grid-community";

interface TableListProps {
    whatIsTable: string;
    activeSchemaIndex: number;
    schemas: SchemaModel[];
}

const EmptyView = () => {
    return (
        <Box p={3}>
            <StriimTypography variant="body4" color="greyscale.700">
                Sorry, could not find any tables.
            </StriimTypography>
        </Box>
    );
};

const CustomTooltip = (props: ITooltipParams) => {
    const data = props.data as TableModel;
    const errors= data.get("errors");

    if (!errors) return null;

    const violations = errors.violations || [];
    if (violations.length === 0) return null;

    return (
        <Box sx={sxStyles.customTooltip}>
            {violations.map((violation, index) => (
                <StriimTypography key={index} variant="caption3" color="greyscale.50" sx={{ display: "block" }}>
                    {index + 1}. {violation.errorDescription}
                </StriimTypography>
            ))}
        </Box>
    );
};

const TablesList: React.FC<TableListProps> = ({ whatIsTable, activeSchemaIndex, schemas }) => {
    const activeSchema = schemas && schemas[activeSchemaIndex];
    const tables = useMemo(() => activeSchema?.get("tables"), [activeSchema]);
    const gridRef = useRef<GridApi<TableModel> | null>(null);
    const [searchText, setSearchText] = useState("");
    const [selectedCount, setSelectedCount] = useState(0);

    const tablesData = useMemo(() => tables?.models || [], [tables]);

    const columnDefs = useMemo(
        () => [
            {
                headerName: "",
                width: 70,
                lockPosition: true,
                suppressMovable: true,
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
                checkboxSelection: (params: ICellRendererParams<TableModel>) => {
                    return params.data.get("isValid");
                },
                cellStyle: {
                    display: "flex",
                    alignItems: "center"
                },
                cellRenderer: (params: ICellRendererParams<TableModel>) => {
                    const table = params.data;
                    if (table.get("isValid")) return null;

                    const errors: TableErrors = table.get("errors");
                    if (errors) {
                        return (
                            <Box
                                component="img"
                                src="app/images/Block.svg"
                                sx={{
                                    width: 20,
                                    height: 20,
                                    cursor: "not-allowed"
                                }}
                            />
                        );
                    }
                    return null;
                }
            },
            {
                headerName: `${whatIsTable} Name`,
                field: "name",
                valueGetter: (params: any) => params.data?.get("name"),
                flex: 1,
                suppressMovable: true,
                resizable: false,
                cellRenderer: (params: ICellRendererParams<TableModel>) => {
                    const table = params.data;
                    return (
                        <StriimTypography
                            variant="body4"
                            sx={{
                                color: table.get("isValid") ? "greyscale.900" : "greyscale.500"
                            }}
                        >
                            {table.get("name")}
                        </StriimTypography>
                    );
                }
            }
        ],
        [whatIsTable]
    );

    const onGridReady = useCallback((params: GridReadyEvent<TableModel>) => {
        if (gridRef.current) {
            gridRef.current = params.api;
        }
    }, []);

    const onFirstDataRendered = useCallback(
        (params: FirstDataRenderedEvent<TableModel>) => {
            const tablesToSelect = [];
            params.api.forEachNode((node :RowNode<TableModel>) => {
                if (node.data && node.data.get("isSelected") && node.data.get("isValid")) {
                    tablesToSelect.push(node);
                }
            });

            if (tablesToSelect.length > 0) {
                params.api.setNodesSelected({ nodes: tablesToSelect, newValue: true });
            }

            const hasSelectedTables = schemas.some(
                (schema: SchemaModel) => schema.get("isEnabled") && schema.get("tables").where({ isSelected: true }).length > 0
            );
            App.vent.trigger(hasSelectedTables ? "appwizard:next:enable" : "appwizard:next:disable");
            setSelectedCount(params.api.getSelectedNodes().length);
        },
        [schemas]
    );

    const onRowSelected = useCallback((event: RowSelectedEvent<TableModel>) => {
        const table = event.data;
        if (table) {
            const isSelected = event.node.isSelected();
            table.set("isSelected", isSelected);
        }
    }, []);

    const onSelectionChanged = useCallback((_event: SelectionChangedEvent<TableModel>) => {
        const hasSelectedTables = schemas.some(
            (schema: SchemaModel) => schema.get("isEnabled") && schema.get("tables").where({ isSelected: true }).length > 0
        );

        App.vent.trigger(hasSelectedTables ? "appwizard:next:enable" : "appwizard:next:disable");
        setSelectedCount(_event.api.getSelectedNodes().length);
    }, [schemas]);

    const defaultColDef = useMemo(
            () => ({
                tooltipComponent: CustomTooltip,
                tooltipValueGetter: (params: any) => {
                    const isValid = params.data.get("isValid");
                    if (isValid) return null;
                    return params.data.get("errors");
                }
            }),
            []
        );


    return (
        <>
            {tables.models.length === 0 ? (
                <EmptyView />
            ) : (
                <Box sx={sxStyles.tableContainer}>
                    <Box sx={sxStyles.tableDescContainer}>
                        <StriimTypography variant="body4" color="greyscale.700" sx={{ wordBreak: "break-all" }}>
                            Please select the list of {whatIsTable}s to load from {activeSchema.get("schemaDisplayName")}
                        </StriimTypography>
                        <StriimInputField
                            name="search-tables"
                            onChange={setSearchText}
                            placeholder={`Search ${whatIsTable}s...`}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon opacity={0.45} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ minWidth: 260 }}
                        />
                        <StriimTypography variant="body4" color="greyscale.700" sx={{ wordBreak: "break-all" }}>
                            {whatIsTable}s selected : {selectedCount} / {tablesData.length}
                        </StriimTypography>
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                        <StriimDataTable
                            key={activeSchema.get("id")}
                            ref={gridRef}
                            columns={columnDefs}
                            data={tablesData}
                            quickFilterText={searchText}
                            getRowId={(params: GetRowIdParams) => params.data.get("id")}
                            rowSelection="multiple"
                            suppressRowClickSelection={false}
                            rowMultiSelectWithClick={true}
                            rowHeight={44}
                            onGridReady={onGridReady}
                            onFirstDataRendered={onFirstDataRendered}
                            onRowSelected={onRowSelected}
                            onSelectionChanged={onSelectionChanged}
                            isRowSelectable={(params) => {
                                const isValid = params.data.get("isValid");
                                return isValid;
                            }}
                            rowClassRules={{
                                "row-selection-disabled": (params) => {
                                    const isValid = params.data.get("isValid");
                                    return !isValid;
                                }
                            }}
                            defaultColDef={defaultColDef}
                        />
                    </Box>
                </Box>
            )}
        </>
    );
};

export default TablesList;
